import React from 'react';
import { ResumeData } from '../../../types/resume';
import { Section } from './shared/Section';
import { ContactInfo } from './shared/ContactInfo';
import { SkillsList } from './shared/SkillsList';

export function ProfessionalTemplate({ data }: { data: ResumeData }) {
  const { personalInfo, experience, education, skills } = data;

  if (!personalInfo) return null;

  return (
    <div className="resume-page">
      {/* Header */}
      <header className="bg-blue-900 text-white px-6 py-4 -mx-6 -mt-6 mb-6">
        <h1 className="resume-text-xl font-medium mb-1">{personalInfo.fullName}</h1>
        <p className="resume-text-sm mb-2">{personalInfo.title}</p>
        <ContactInfo 
          info={personalInfo} 
          className="text-blue-50" 
          iconClassName="h-3 w-3 text-blue-200"
        />
      </header>

      {/* Two Column Layout */}
      <div className="grid grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Summary */}
          {personalInfo.summary && (
            <Section title="Profile">
              <p className="resume-text-sm text-gray-700">{personalInfo.summary}</p>
            </Section>
          )}

          {/* Skills */}
          {skills && skills.length > 0 && (
            <Section title="Skills">
              <SkillsList 
                skills={skills}
                showLevel={true}
              />
            </Section>
          )}

          {/* Education */}
          {education && education.length > 0 && (
            <Section title="Education">
              <div className="space-y-3">
                {education.map((edu) => (
                  <div key={edu.id}>
                    <h3 className="resume-text-sm font-medium">{edu.school}</h3>
                    <p className="resume-text-xs text-gray-700">{edu.degree} in {edu.field}</p>
                    <p className="resume-text-xs text-gray-500">
                      {edu.graduationDate}
                      {edu.gpa && ` • GPA: ${edu.gpa}`}
                    </p>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Main Content */}
        <div className="col-span-2">
          {/* Experience */}
          {experience && experience.length > 0 && (
            <Section title="Professional Experience">
              <div className="space-y-4">
                {experience.map((exp) => (
                  <div key={exp.id}>
                    <div className="flex justify-between items-baseline mb-1">
                      <div>
                        <h3 className="resume-text-sm font-medium text-blue-900">{exp.position}</h3>
                        <p className="resume-text-xs text-blue-800">{exp.company} • {exp.location}</p>
                      </div>
                      <span className="resume-text-xs text-gray-500">
                        {exp.startDate} – {exp.current ? 'Present' : exp.endDate}
                      </span>
                    </div>
                    <p className="resume-text-xs text-gray-700 mb-1">{exp.description}</p>
                    {exp.achievements.length > 0 && (
                      <ul className="resume-text-xs text-gray-700 list-disc list-inside">
                        {exp.achievements.map((achievement, index) => (
                          <li key={index}>{achievement}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}